import { useEffect, useId, useRef, useState, type ReactNode } from 'react'
import { Link } from '@tanstack/react-router'
import { ChevronLeft, ChevronRight, Search, SlidersHorizontal, X } from 'lucide-react'
import { secondaryButton, small } from '@/components/shell/StatusScreens'
import { cn } from '@/lib/utils'
import { clampPage, clampPulse, PAGE_SIZE, pageCount, pageRange, pagerLabel } from './calm-logic'

/* ERP · peças "calmas" que todas as telas do Staff usam (pesquisa de UX, SPEC parte 5): uma frase diz o estado, poucos
   números, uma busca + uma linha de segmentos, linhas compactas, listas de 20 em 20 e a explicação escondida num só
   "Como funciona". Fundo branco, corpo 13–13,5px, números até 20px.

   StateHeader({ eyebrow, title, sub?, pulse?, actions? })
     eyebrow: texto curto acima (ex.: "Sponsor · Padrinhos e doadores"); title: UMA frase com o estado (ReactNode, ≤23px);
     sub: no máximo uma linha apagada; pulse: até 3 { label, value } numa linha apagada (o resto é cortado);
     actions: botões à direita.
   SegmentChips({ value, options: [{ value, label, count? }], onChange, label? })  — uma linha de segmentos (aria-pressed).
   SearchBox({ value, onChange, placeholder?, label? })  — a única busca da tela (Esc limpa).
   CompactRow({ lead?, title, meta?, right?, action?, to? })  — linha de ~44px: ícone/avatar, título, meta apagada,
     valor à direita e uma ação; com `to` a linha inteira vira link.
   CompactList({ label, children, empty? })  — painel branco com as CompactRow (empty = texto quando não há linhas).
   Pager({ page })  — paginação de 16 linhas: ‹ "17–32 de 134" ›; some quando cabe numa página.
     usePager(total) devolve { start, end, page, pages, total, go, reset }: a tela mostra lista.slice(start, end);
     reset volta à 1ª página (use quando a busca ou o segmento mudam).
   HowItWorks({ children, summary? })  — a explicação da tela num <details> "Como funciona".
   FiltersPopover({ active?, children, label? })  — filtros raros num menu pequeno (Esc e clique fora fecham).
   QuietState({ title, children? })  — estado honesto pequeno ("Ainda não integrado", "Nada por aqui"). */

export type PulseItem = { label: string; value: ReactNode }

export function StateHeader({ eyebrow, title, sub, pulse, actions }: { eyebrow: ReactNode; title: ReactNode; sub?: ReactNode; pulse?: readonly PulseItem[]; actions?: ReactNode }) {
    const items = clampPulse(pulse)
    return (
        <section aria-labelledby="state-title" className="flex flex-wrap items-end justify-between gap-x-4 gap-y-2.5">
            <div className="flex min-w-0 flex-[1_1_420px] flex-col gap-1">
                <span className="text-[12px] font-medium text-muted">{eyebrow}</span>
                <h1 id="state-title" className="m-0 max-w-[60ch] text-[22px] leading-[1.25] font-semibold tracking-[-0.01em] text-balance">{title}</h1>
                {sub && <p className="m-0 text-[13px] text-muted">{sub}</p>}
                {items.length > 0 && (
                    <p className="m-0 flex flex-wrap gap-x-1.5 text-[13px] text-muted">
                        {items.map((p, i) => (
                            <span key={p.label}>
                                {i > 0 && <span aria-hidden="true" className="mr-1.5">·</span>}
                                <b className="tabular font-semibold text-fg">{p.value}</b> {p.label}
                            </span>
                        ))}
                    </p>
                )}
            </div>
            {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
        </section>
    )
}

export type SegmentOption<T extends string> = { value: T; label: string; count?: number }

export function SegmentChips<T extends string>({ value, options, onChange, label = 'Segmento' }: { value: T; options: readonly SegmentOption<T>[]; onChange: (v: T) => void; label?: string }) {
    return (
        <div role="group" aria-label={label} className="flex flex-wrap gap-1.5">
            {options.map(o => {
                const on = o.value === value
                return (
                    <button
                        key={o.value}
                        type="button"
                        aria-pressed={on}
                        onClick={() => onChange(o.value)}
                        className={cn('inline-flex min-h-8 cursor-pointer items-center gap-1.5 rounded-full border px-3 text-[13px] whitespace-nowrap', on ? 'font-medium text-fg' : 'bg-panel text-muted hover:bg-[var(--hover)]')}
                        style={on ? { background: 'var(--tint)', borderColor: 'color-mix(in srgb, var(--green) 35%, var(--line))' } : { borderColor: 'var(--line)' }}
                    >
                        {o.label}
                        {o.count !== undefined && <span className="tabular text-[12px] font-normal text-muted">{o.count.toLocaleString('pt-BR')}</span>}
                    </button>
                )
            })}
        </div>
    )
}

export function SearchBox({ value, onChange, placeholder = 'Buscar…', label = 'Buscar', className }: { value: string; onChange: (v: string) => void; placeholder?: string; label?: string; className?: string }) {
    return (
        <label className={cn('relative flex min-h-9 w-full max-w-[360px] items-center rounded-[10px] border border-line bg-panel focus-within:border-[var(--green)]', className)}>
            <span className="sr-only">{label}</span>
            <Search size={15} aria-hidden="true" className="pointer-events-none absolute left-2.5 text-muted" />
            <input
                type="search"
                value={value}
                placeholder={placeholder}
                onChange={e => onChange(e.target.value)}
                onKeyDown={e => { if (e.key === 'Escape' && value) { e.preventDefault(); onChange('') } }}
                className="min-h-9 w-full rounded-[10px] border-0 bg-transparent pr-8 pl-8 text-[13.5px] text-fg outline-none placeholder:text-muted"
            />
            {value && (
                <button type="button" aria-label="Limpar a busca" onClick={() => onChange('')} className="absolute right-1.5 grid size-6 cursor-pointer place-items-center rounded-[6px] border-0 bg-transparent text-muted hover:bg-[var(--hover)]">
                    <X size={14} aria-hidden="true" />
                </button>
            )}
        </label>
    )
}

export function CompactRow({ lead, title, meta, right, action, to }: { lead?: ReactNode; title: ReactNode; meta?: ReactNode; right?: ReactNode; action?: ReactNode; to?: string }) {
    const body = (
        <>
            {lead && <span className="grid size-7 flex-none place-items-center text-muted">{lead}</span>}
            <span className="flex min-w-0 flex-1 flex-col leading-[1.3]">
                <span className="truncate text-[13.5px] font-medium text-fg">{title}</span>
                {meta && <span className="truncate text-[12.5px] text-muted">{meta}</span>}
            </span>
            {right && <span className="tabular flex-none text-right text-[13px] text-fg">{right}</span>}
            {to && !action && <ChevronRight size={15} aria-hidden="true" className="flex-none text-muted" />}
        </>
    )
    return (
        <li className="flex min-h-11 items-center gap-2 border-t border-line first:border-t-0">
            {to ? (
                <Link to={to as '/'} className="flex min-h-11 min-w-0 flex-1 items-center gap-3 rounded-[8px] px-1.5 py-1 no-underline hover:bg-[var(--hover)]">{body}</Link>
            ) : (
                <div className="flex min-h-11 min-w-0 flex-1 items-center gap-3 px-1.5 py-1">{body}</div>
            )}
            {action && <span className="flex flex-none items-center gap-1.5 pr-1">{action}</span>}
        </li>
    )
}

export function CompactList({ label, children, empty, className }: { label: string; children?: ReactNode; empty?: ReactNode; className?: string }) {
    const hasRows = Array.isArray(children) ? children.some(Boolean) : !!children
    return (
        <div className={cn('min-w-0 rounded-[14px] border border-line bg-panel px-2.5 py-1', className)}>
            {hasRows ? (
                <ul aria-label={label} className="m-0 flex list-none flex-col p-0">{children}</ul>
            ) : (
                <p className="m-0 px-1.5 py-3 text-[13px] text-muted">{empty ?? 'Nada por aqui.'}</p>
            )}
        </div>
    )
}

export type PagerState = { start: number; end: number; page: number; pages: number; total: number; size: number; go: (page: number) => void; reset: () => void }

export function Pager({ page }: { page: PagerState }) {
    const text = pagerLabel(page.page, page.total, page.size)
    if (!text) return null
    const btn = cn(secondaryButton, small, 'size-8 justify-center px-0 disabled:opacity-40')
    return (
        <nav aria-label="Paginação" className="flex items-center justify-center gap-2 pt-1">
            <button type="button" aria-label="Página anterior" disabled={page.page === 0} onClick={() => page.go(page.page - 1)} className={btn}>
                <ChevronLeft size={16} aria-hidden="true" />
            </button>
            <span aria-live="polite" className="tabular min-w-[120px] text-center text-[13px] text-muted">{text}</span>
            <button type="button" aria-label="Próxima página" disabled={page.page >= page.pages - 1} onClick={() => page.go(page.page + 1)} className={btn}>
                <ChevronRight size={16} aria-hidden="true" />
            </button>
        </nav>
    )
}

/** Páginas de 16; a página fica dentro do intervalo quando a lista encolhe. */
export function usePager(total: number, size = PAGE_SIZE): PagerState {
    const [raw, setRaw] = useState(0)
    const page = clampPage(raw, total, size)
    const { start, end } = pageRange(page, total, size)
    return { start, end, page, pages: pageCount(total, size), total, size, go: p => setRaw(clampPage(p, total, size)), reset: () => setRaw(0) }
}

export function HowItWorks({ children, summary = 'Como funciona' }: { children: ReactNode; summary?: string }) {
    return (
        <details className="group rounded-[12px] border border-line bg-panel px-3.5 py-2 text-[13px] text-muted">
            <summary className="flex cursor-pointer list-none items-center gap-1.5 font-medium text-fg [&::-webkit-details-marker]:hidden">
                <ChevronRight size={14} aria-hidden="true" className="flex-none text-muted transition-transform group-open:rotate-90 motion-reduce:transition-none" />
                {summary}
            </summary>
            <div className="flex flex-col gap-1.5 pt-2 pb-1 leading-[1.5]">{children}</div>
        </details>
    )
}

export function FiltersPopover({ active = 0, children, label = 'Filtros' }: { active?: number; children: ReactNode; label?: string }) {
    const [open, setOpen] = useState(false)
    const ref = useRef<HTMLDivElement>(null)
    const id = useId()
    useEffect(() => {
        if (!open) return
        const onKey = (e: KeyboardEvent) => {
            if (e.key !== 'Escape') return
            setOpen(false)
            ref.current?.querySelector<HTMLButtonElement>('button')?.focus()
        }
        const onDown = (e: PointerEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false) }
        document.addEventListener('keydown', onKey)
        document.addEventListener('pointerdown', onDown)
        return () => {
            document.removeEventListener('keydown', onKey)
            document.removeEventListener('pointerdown', onDown)
        }
    }, [open])
    return (
        <div ref={ref} className="relative">
            <button type="button" aria-expanded={open} aria-controls={id} onClick={() => setOpen(v => !v)} className={cn(secondaryButton, small)}>
                <SlidersHorizontal size={14} aria-hidden="true" />
                {label}
                {active > 0 && <span className="tabular rounded-full bg-tint px-1.5 text-[11.5px] leading-[18px] font-semibold text-fg">{active}</span>}
            </button>
            {open && (
                <div
                    id={id}
                    role="dialog"
                    aria-label={label}
                    className="absolute top-full right-0 z-40 mt-1 flex min-w-[240px] flex-col gap-2.5 rounded-[12px] border border-line bg-panel p-3 text-[13px]"
                    style={{ boxShadow: '0 12px 32px var(--shadow), 0 2px 6px var(--shadow)' }}
                >
                    {children}
                </div>
            )}
        </div>
    )
}

export function QuietState({ title, children }: { title: ReactNode; children?: ReactNode }) {
    return (
        <div className="flex flex-col gap-1 rounded-[14px] border border-dashed border-line-2 bg-panel px-4 py-3.5">
            <span className="text-[13.5px] font-medium text-fg">{title}</span>
            {children && <div className="text-[13px] leading-[1.5] text-muted">{children}</div>}
        </div>
    )
}
